"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  applyConsent,
  OPEN_COOKIE_SETTINGS_EVENT,
  readStoredConsent,
} from "@/lib/consent";
import { isCompanionView } from "@/lib/instance-path";

/**
 * First-layer consent banner for Google Tag Manager. Shown when no valid
 * consent cookie exists, and reopenable via the footer's "Cookie settings"
 * link (OPEN_COOKIE_SETTINGS_EVENT). "Accept all" and "Reject all" sit on
 * the first layer with equal weight — one click each, as the ePrivacy
 * guidance requires.
 *
 * Placement: bottom of the viewport (bottom-right on desktop) — except for
 * visitors on the landing_onboarding "onboarding" arm, where the pre-auth
 * setup chat's reply input lives exactly there. For them the banner floats
 * above center on mobile and bottom-left on desktop so the chat stays
 * usable. `preauthChat` carries the server-side assignment (anonymous +
 * onboarding arm); the ?exp_ preview override is honored client-side on
 * top, mirroring PreauthChatWidget.
 *
 * Never in the companion view (the person's own chat, framed in the browser
 * side panel): nothing there needs asking. With no answer, nothing it asks
 * about runs — Consent Mode defaults to denied and PostHog captures nothing
 * (lib/posthog-events.ts) — and an answer given on the site applies in the
 * panel too, since the frame has the site's own cookies. The card would also
 * sit over the chat's message box in a narrow frame. Settings stay on the
 * site, behind the footer's link.
 */
export function CookieConsent({ preauthChat }: { preauthChat: boolean }) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [clearOfChat, setClearOfChat] = useState(false);
  const companion = isCompanionView(usePathname());

  useEffect(() => {
    const override = new URLSearchParams(window.location.search).get("exp_landing_onboarding_v1");
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount read of location.search (no SSR value), mirroring PreauthChatWidget; the banner reveals post-hydration anyway
    setClearOfChat(override === "onboarding" || (preauthChat && override !== "control"));
  }, [preauthChat]);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- SSR-safe localStorage read on mount; consent state cannot be read during render
      setOpen(true);
      return;
    }
    setAnalytics(stored.analytics);
    setMarketing(stored.marketing);
  }, []);

  useEffect(() => {
    const reopen = () => {
      const stored = readStoredConsent();
      if (stored) {
        setAnalytics(stored.analytics);
        setMarketing(stored.marketing);
        setCustomize(true);
      }
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    return () => { window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen); };
  }, []);

  if (!open || companion) return null;

  const choose = (choice: { analytics: boolean; marketing: boolean }) => {
    applyConsent(choice);
    setAnalytics(choice.analytics);
    setMarketing(choice.marketing);
    setOpen(false);
    setCustomize(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t`Cookie consent`}
      className={`fixed inset-x-4 z-50 mx-auto max-w-md rounded-xl border border-ink-mid bg-ink-soft p-5 text-cream shadow-2xl ${
        clearOfChat
          ? "top-[40%] -translate-y-1/2 sm:top-auto sm:bottom-4 sm:left-6 sm:right-auto sm:translate-y-0"
          : "bottom-4 sm:right-6 sm:left-auto"
      }`}
    >
      <p className="text-sm leading-relaxed text-stone-soft">
        <Trans>
          We use cookies for analytics and to measure our advertising, in
          addition to the strictly necessary ones that keep you signed in.
          Details in our{" "}
          <Link href="/privacy" className="underline hover:text-cream">
            privacy policy
          </Link>
          .
        </Trans>
      </p>

      {customize && (
        <div className="mt-4 space-y-3 text-sm">
          <label className="flex items-center justify-between gap-4">
            <span>
              <span className="block"><Trans>Strictly necessary</Trans></span>
              <span className="block text-xs text-stone">
                <Trans>Sign-in and session — always on</Trans>
              </span>
            </span>
            <input type="checkbox" checked disabled className="accent-signal" />
          </label>
          <label className="flex items-center justify-between gap-4">
            <span>
              <span className="block"><Trans>Analytics</Trans></span>
              <span className="block text-xs text-stone">
                <Trans>Product analytics and usage statistics</Trans>
              </span>
            </span>
            <input
              type="checkbox"
              checked={analytics}
              onChange={(e) => { setAnalytics(e.target.checked); }}
              className="accent-signal"
            />
          </label>
          <label className="flex items-center justify-between gap-4">
            <span>
              <span className="block"><Trans>Marketing</Trans></span>
              <span className="block text-xs text-stone">
                <Trans>Google Ads conversion measurement</Trans>
              </span>
            </span>
            <input
              type="checkbox"
              checked={marketing}
              onChange={(e) => { setMarketing(e.target.checked); }}
              className="accent-signal"
            />
          </label>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => { choose({ analytics: true, marketing: true }); }}
          className="rounded-md bg-cream px-4 py-2 text-sm font-medium text-ink transition hover:bg-cream-warm"
        >
          <Trans>Accept all</Trans>
        </button>
        <button
          type="button"
          onClick={() => { choose({ analytics: false, marketing: false }); }}
          className="rounded-md border border-cream/40 px-4 py-2 text-sm font-medium text-cream transition hover:border-cream"
        >
          <Trans>Reject all</Trans>
        </button>
        {customize ? (
          <button
            type="button"
            onClick={() => { choose({ analytics, marketing }); }}
            className="rounded-md border border-cream/40 px-4 py-2 text-sm font-medium text-cream transition hover:border-cream"
          >
            <Trans>Save choices</Trans>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => { setCustomize(true); }}
            className="px-2 py-2 text-sm text-stone-soft underline transition hover:text-cream"
          >
            <Trans>Customize</Trans>
          </button>
        )}
      </div>
    </div>
  );
}
